import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  Chip,
  Avatar,
  Divider,
  Tooltip,
  Badge,
  alpha,
  Checkbox,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { styled } from "@mui/system";
import {
  CreateOutlined,
  DeleteOutline,
  EmailOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  PersonOutlined,
  VisibilityOutlined,
  PhoneOutlined,
  WorkOutlined,
  LanguageOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  BlockOutlined,
} from "@mui/icons-material";
import { COLORS } from "colors/color";
import colors from "colors/color";
import { OutlinedButton } from "components/Buttons/Buttons";
import { useTranslation } from "react-i18next";

// Enhanced Card with selection support and responsive design
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => !['viewMode', 'isSelected', 'isSelectable', 'isActive'].includes(prop),
})(({ theme, viewMode, isSelected, isSelectable, isActive }) => ({
  position: "relative",
  display: "flex",
  flexDirection: viewMode === 'list' ? 'row' : 'column',
  height: viewMode === 'list' ? 'auto' : '100%',
  width: '100%',
  borderRadius: viewMode === 'list' ? 12 : 20,
  background: 'none',
  border: isSelected
    ? `2px solid ${colors.primary}`
    : `1px solid ${alpha(colors.primary, 0.08)}`,
  overflow: "hidden",
  boxShadow: 'none',
  // transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: (isSelectable && isActive) ? 'pointer' : 'default',
  opacity: isActive ? 1 : 0.7,

  // Mobile responsive adjustments
  [theme.breakpoints.down('sm')]: {
    borderRadius: viewMode === 'list' ? 8 : 16,
    flexDirection: 'column', // Force column layout on mobile even for list view
  },

  "&:hover": {
    boxShadow: 'none',
    border: isSelected
      ? `2px solid ${colors.primary}`
      : `1px solid ${alpha(colors.primary, 0.15)}`,
    opacity: isActive ? 1 : 0.7,
  },
}));

const ListViewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  minHeight: 80,

  // Mobile responsive adjustments
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
    minHeight: 'auto',
  },

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
}));

const SelectionCheckbox = styled(Checkbox)(({ theme }) => ({
  zIndex: 2,
  '&:hover': {
    backgroundColor: alpha(colors.primary, 0.04),
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
}));

const ListCheckboxContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 40,

  [theme.breakpoints.down('sm')]: {
    alignSelf: 'flex-end',
    minWidth: 'auto',
  },
}));

const ListAvatarContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minWidth: 60,
  height: 60,

  [theme.breakpoints.down('md')]: {
    minWidth: 50,
    height: 50,
  },

  [theme.breakpoints.down('sm')]: {
    alignSelf: 'center',
    minWidth: 60,
    height: 60,
  },
}));

const ListContentContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    alignItems: 'center',
    textAlign: 'center',
  },
}));

const ListActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 'fit-content',

  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(0.5),
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const StyledCardMedia = styled(CardMedia, {
  shouldForwardProp: (prop) => prop !== 'viewMode',
})(({ theme, viewMode }) => ({
  width: viewMode === 'list' ? 120 : '100%',
  height: viewMode === 'list' ? 120 : 200,
  objectFit: "cover",
  flexShrink: 0,

  [theme.breakpoints.down('lg')]: {
    width: viewMode === 'list' ? 100 : '100%',
    height: viewMode === 'list' ? 100 : 180,
  },

  [theme.breakpoints.down('md')]: {
    width: viewMode === 'list' ? 80 : '100%',
    height: viewMode === 'list' ? 80 : 160,
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 140,
  },
}));

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'viewMode',
})(({ theme, viewMode }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: viewMode === 'list' ? '16px 20px' : '20px',

  [theme.breakpoints.down('md')]: {
    padding: viewMode === 'list' ? '12px 16px' : '16px',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const StatusBadge = styled(Box)(({ theme, status }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: status === 'active' ? '#10b981' : '#ef4444',
  border: '2px solid white',
  boxShadow: `0 2px 8px ${alpha(status === 'active' ? '#10b981' : '#ef4444', 0.3)}`,

  [theme.breakpoints.down('sm')]: {
    top: 8,
    right: 8,
    width: 10,
    height: 10,
  },
}));

// Responsive Typography component
const ResponsiveTypography = styled(Typography)(({ theme, variant }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: variant === 'h5' ? '1.25rem' :
      variant === 'h6' ? '1.1rem' :
        variant === 'body2' ? '0.8rem' : 'inherit',
  },
}));

function RecruiterCard({
  recruiterId,
  imageSrc,
  firstName,
  lastName,
  email,
  company,
  address,
  city,
  country,
  postal_code,
  comp_industry,
  comp_description,
  onDeleteRecruiter,
  updateRecruiter,
  onSelectRecruiter,
  viewMode = 'list',
  isActive = true,
  isSelectable = true,
  isSelected = false,
  onToggleSelection,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [recruiterData, setRecruiterData] = useState({
    firstName,
    lastName,
    email,
    company,
    address,
    city,
    country,
    postal_code,
    comp_industry,
    comp_description,
  });
  const { t } = useTranslation();

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setRecruiterData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUpdateRecruiter = () => {
    setOpen(false);
    const updatedRecruiterData = {
      recruiterId,
      first_name: recruiterData.firstName,
      last_name: recruiterData.lastName,
      email: recruiterData.email,
      address: recruiterData.address,
      city: recruiterData.city,
      country: recruiterData.country,
      postal_code: recruiterData.postal_code,
      comp_industry: recruiterData.comp_industry,
      comp_description: recruiterData.comp_description,
    };
    updateRecruiter(updatedRecruiterData);
  };

  const fullName = `${firstName} ${lastName}`;
  const hasLocation = city || country;
  const locationText = [city, country].filter(Boolean).join(', ');

  const handleCardClick = () => {
    if (isSelectable && isActive && onToggleSelection) {
      onToggleSelection(recruiterId);
    }
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    if (isActive && onToggleSelection) {
      onToggleSelection(recruiterId);
    }
  };

  // Force grid view on mobile for better UX
  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  // Render list view layout (but force grid on mobile)
  if (effectiveViewMode === 'list') {
    return (
      <StyledCard
        viewMode={viewMode}
        isSelected={isSelected}
        isSelectable={isSelectable}
        isActive={isActive}
        onClick={handleCardClick}
      >
        <ListViewContainer isSelected={isSelected}>
          {/* Checkbox */}
          <ListCheckboxContainer>
            <SelectionCheckbox
              checked={isSelected}
              onChange={handleCheckboxClick}
              onClick={handleCheckboxClick}
              disabled={!isActive}
              sx={{
                color: isSelected ? colors.primary : alpha(colors.primary, 0.5),
                '&.Mui-checked': {
                  color: colors.primary,
                },
                '&.Mui-disabled': {
                  color: alpha(colors.primary, 0.3),
                },
              }}
            />
          </ListCheckboxContainer>

          {/* Avatar/Image */}
          <ListAvatarContainer>
            <Avatar
              src={imageSrc}
              alt={fullName}
              sx={{
                width: { xs: 60, md: 50, lg: 60 },
                height: { xs: 60, md: 50, lg: 60 },
                border: `2px solid ${alpha(colors.primary, 0.1)}`,
              }}
              onError={(e) => {
                e.target.src = '/default-avatar.png';
              }}
            >
              {firstName[0]}{lastName[0]}
            </Avatar>
            <StatusBadge
              status={isActive ? 'active' : 'inactive'}
              sx={{ top: 4, right: 4 }}
            />
          </ListAvatarContainer>

          {/* Main Content */}
          <ListContentContainer>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
              <ResponsiveTypography
                variant="h6"
                sx={{
                  color: colors.primary,
                  fontWeight: 700,
                  fontFamily: 'Poppins',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                }}
              >
                {fullName}
              </ResponsiveTypography>
              {isActive ? (
                <CheckCircleOutlined sx={{ color: '#10b981', fontSize: { xs: 14, md: 16 } }} />
              ) : (
                <ErrorOutlineOutlined sx={{ color: '#ef4444', fontSize: { xs: 14, md: 16 } }} />
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems="center">
              {/* Email */}
              <Box display="flex" alignItems="center" gap={0.5}>
                <EmailOutlined sx={{ color: alpha(colors.primary, 0.6), fontSize: { xs: 14, md: 16 } }} />
                <ResponsiveTypography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'Poppins',
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    maxWidth: { xs: 'none', md: 200 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {email}
                </ResponsiveTypography>
              </Box>
            </Stack>
          </ListContentContainer>

          {/* Actions */}
          <ListActionsContainer>
            {/* <OutlinedButton
              onClick={(e) => {
                e.stopPropagation();
                if (isActive) {
                  onSelectRecruiter(recruiterId);
                }
              }}
              startIcon={<VisibilityOutlined />}
              size="small"
              disabled={!isActive}
             
            >
             
            </OutlinedButton> */}
            <Tooltip title={t('recruiterCard.viewRecruiterHistory')}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (isActive) {
                    onSelectRecruiter(recruiterId);
                  }
                }}
                size="small"
                disabled={!isActive}
                sx={{
                  color: alpha(colors.primary, 0.7),
                  backgroundColor: alpha(colors.primary, 0.05),
                  border: `1px solid ${alpha(colors.primary, 0.15)}`,
                  opacity: isActive ? 1 : 0.5,
                  '&:hover': {
                    color: colors.primary,
                    backgroundColor: alpha(colors.primary, 0.1),
                  },
                  '&.Mui-disabled': {
                    color: alpha(colors.primary, 0.3),
                    backgroundColor: alpha(colors.primary, 0.02),
                  },
                }}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('recruiterCard.editRecruiter')}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (isActive) {
                    setOpen(true);
                  }
                }}
                size="small"
                disabled={!isActive}
                sx={{
                  color: alpha(colors.primary, 0.7),
                  backgroundColor: alpha(colors.primary, 0.05),
                  border: `1px solid ${alpha(colors.primary, 0.15)}`,
                  opacity: isActive ? 1 : 0.5,
                  '&:hover': {
                    color: colors.primary,
                    backgroundColor: alpha(colors.primary, 0.1),
                  },
                  '&.Mui-disabled': {
                    color: alpha(colors.primary, 0.3),
                    backgroundColor: alpha(colors.primary, 0.02),
                  },
                }}
              >
                <CreateOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('recruiterCard.deactivateRecruiter')}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDelete(true);
                }}
                size="small"
                 disabled={!isActive}
                sx={{
                  color: '#ef4444',
                  backgroundColor: alpha('#ef4444', 0.3),
                  border: `1px solid ${alpha('#ef4444', 0.15)}`,
                  '&:hover': {
                    color: '#ef4444',
                    backgroundColor: alpha('#ef4444', 0.4),
                  },
                }}
              >
                <BlockOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListActionsContainer>
        </ListViewContainer>

        {/* Responsive Delete Dialog */}
        <Dialog
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          fullWidth
          maxWidth="xs"
          fullScreen={isMobile}
        >
          <DialogContent sx={{ padding: { xs: 3, sm: 2 } }}>
            <ResponsiveTypography fontSize={{ xs: 16, sm: 18 }}>
              {t('recruiterCard.confirmations.blockTitle')}
            </ResponsiveTypography>
          </DialogContent>
          <DialogActions sx={{ padding: { xs: 2, sm: 1 } }}>
            <Button variant="contained" onClick={() => setOpenDelete(false)}
              size={isMobile ? 'large' : 'medium'}>
              {t('recruiterCard.confirmations.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onDeleteRecruiter(recruiterId);
                setOpenDelete(false);
              }}
              color="error"
              size={isMobile ? 'large' : 'medium'}
            >
              {t('recruiterCard.confirmations.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Responsive Update Dialog */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: `0 20px 60px ${alpha(colors.primary, 0.15)}`,
            },
          }}
        >
          <DialogContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.primary,
                fontFamily: "Poppins",
                fontWeight: 700,
                mb: 3,
              }}
            >
              {t('createRecruiter.update')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                   {t('createRecruiter.fields.firstName')}
                </Typography>
                <TextField
                  fullWidth
                  name="firstName"
                  value={recruiterData.firstName}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                        "& fieldset": {
                          borderColor: colors.primary,
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.primary,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  {t('createRecruiter.fields.lastName')}
                </Typography>
                <TextField
                  fullWidth
                  name="lastName"
                  value={recruiterData.lastName}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                        "& fieldset": {
                          borderColor: colors.primary,
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.primary,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  Email
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  name="email"
                  value={recruiterData.email}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                        "& fieldset": {
                          borderColor: colors.primary,
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.primary,
                    },
                  }}
                />
              </Grid>
             
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => setOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {t('recruiterCard.confirmations.cancel')}
            </Button>
            <Button
              onClick={handleUpdateRecruiter}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontFamily: "Poppins",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${
                  colors.primary
                } 0%, ${alpha(colors.primary, 0.8)} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(
                    colors.primary,
                    0.9
                  )} 0%, ${colors.primary} 100%)`,
                },
              }}
            >
              {t('recruiterCard.updateRecruiter')}
            </Button>
          </DialogActions>
        </Dialog>
      </StyledCard>
    );
  }

  // Grid view layout with responsive design
  return (
    <StyledCard
      viewMode={effectiveViewMode}
      isSelected={isSelected}
      isSelectable={isSelectable}
      isActive={isActive}
      onClick={handleCardClick}
    >
      {isSelectable && (
        <SelectionCheckbox
          checked={isSelected}
          onChange={handleCheckboxClick}
          onClick={handleCheckboxClick}
          disabled={!isActive}
          sx={{
            position: 'absolute',
            top: { xs: 6, sm: 8 },
            left: { xs: 6, sm: 8 },
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '50%',
            padding: { xs: 2, sm: 4 },
            color: isSelected ? colors.primary : alpha(colors.primary, 0.5),
            '&.Mui-checked': {
              color: colors.primary,
            },
            '&.Mui-disabled': {
              color: alpha(colors.primary, 0.3),
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        />
      )}
      <StatusBadge status={isActive ? 'active' : 'inactive'} />

      {/* Profile Image */}
      <Box sx={{ position: 'relative' }}>
        <StyledCardMedia
          component="img"
          image={imageSrc || '/default-avatar.png'}
          alt={fullName}
          viewMode={effectiveViewMode}
          onError={(e) => {
            e.target.src = '/default-avatar.png';
          }}
        />
      </Box>

      <StyledCardContent viewMode={effectiveViewMode}>
        {/* Header Section */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}
            sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0 } }}>
            <ResponsiveTypography
              variant="h5"
              component="div"
              sx={{
                color: colors.primary,
                fontWeight: 700,
                fontFamily: 'Poppins',
                lineHeight: 1.2,
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              {fullName}
            </ResponsiveTypography>
            <Tooltip title={isActive ? t('recruiterCard.activeRecruiter') : t('recruiterCard.inactiveRecruiter')}>
              <Box>
                {isActive ? (
                  <CheckCircleOutlined sx={{ color: '#10b981', fontSize: { xs: 18, sm: 20 } }} />
                ) : (
                  <ErrorOutlineOutlined sx={{ color: '#ef4444', fontSize: { xs: 18, sm: 20 } }} />
                )}
              </Box>
            </Tooltip>
          </Box>

          {/* Contact Information */}
          <Stack spacing={1} mb={2} alignItems={{ xs: 'center', sm: 'flex-start' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailOutlined sx={{ color: alpha(colors.primary, 0.7), fontSize: { xs: 16, sm: 18 } }} />
              <ResponsiveTypography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'Poppins',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                  wordBreak: 'break-all',
                }}
              >
                {email}
              </ResponsiveTypography>
            </Box>
          </Stack>
        </Box>

        {/* Actions Section */}
        <Box>
          <Divider sx={{ mb: 2, borderColor: alpha(colors.primary, 0.1) }} />
          <Stack
            direction="column"
            spacing={1}
            alignItems="stretch"
          >
            <OutlinedButton
              onClick={(e) => {
                e.stopPropagation();
                if (isActive) {
                  onSelectRecruiter(recruiterId);
                }
              }}
              startIcon={<VisibilityOutlined />}
              fullWidth
              size={isMobile ? 'large' : 'medium'}
              disabled={!isActive}
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem' },
                opacity: isActive ? 1 : 0.5,
              }}
            >
              {t('recruiterCard.viewDetails')}
            </OutlinedButton>

            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
            >
              <Tooltip title={t('recruiterCard.editRecruiter')}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isActive) {
                      setOpen(true);
                    }
                  }}
                  size={isMobile ? 'medium' : 'small'}
                  disabled={!isActive}
                  sx={{
                    color: alpha(colors.primary, 0.7),
                    backgroundColor: alpha(colors.primary, 0.05),
                    border: `1px solid ${alpha(colors.primary, 0.15)}`,
                    opacity: isActive ? 1 : 0.5,
                    '&:hover': {
                      color: colors.primary,
                      backgroundColor: alpha(colors.primary, 0.1),
                    },
                    '&.Mui-disabled': {
                      color: alpha(colors.primary, 0.3),
                      backgroundColor: alpha(colors.primary, 0.02),
                    },
                  }}
                >
                  <CreateOutlined fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('recruiterCard.deleteRecruiter')}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDelete(true);
                  }}
                  size={isMobile ? 'medium' : 'small'}
                  sx={{
                    color: alpha('#ef4444', 0.7),
                    backgroundColor: alpha('#ef4444', 0.05),
                    border: `1px solid ${alpha('#ef4444', 0.15)}`,
                    '&:hover': {
                      color: '#ef4444',
                      backgroundColor: alpha('#ef4444', 0.1),
                    },
                  }}
                >
                  <BlockOutlined fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </StyledCardContent>

      {/* Responsive Dialogs for grid view */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogContent sx={{ padding: { xs: 3, sm: 2 } }}>
          <ResponsiveTypography fontSize={{ xs: 16, sm: 18 }}>
            {t('recruiterCard.confirmations.deleteTitle')}
          </ResponsiveTypography>
        </DialogContent>
        <DialogActions sx={{ padding: { xs: 2, sm: 1 } }}>
          <Button variant="contained" onClick={() => setOpenDelete(false)}
            size={isMobile ? 'large' : 'medium'}>
            {t('recruiterCard.confirmations.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onDeleteRecruiter(recruiterId);
              setOpenDelete(false);
            }}
            color="error"
            size={isMobile ? 'large' : 'medium'}
          >
            {t('recruiterCard.confirmations.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Responsive Update Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 20px 60px ${alpha(colors.primary, 0.15)}`,
          },
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary,
              fontFamily: "Poppins",
              fontWeight: 700,
              mb: 3,
            }}
          >
            {t('recruiterCard.updateRecruiter')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                First Name
              </Typography>
              <TextField
                fullWidth
                name="firstName"
                value={recruiterData.firstName}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 12,
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                    },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                      "& fieldset": {
                        borderColor: colors.primary,
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Last Name
              </Typography>
              <TextField
                fullWidth
                name="lastName"
                value={recruiterData.lastName}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 12,
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                    },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                      "& fieldset": {
                        borderColor: colors.primary,
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                name="email"
                value={recruiterData.email}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 12,
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                    },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                      "& fieldset": {
                        borderColor: colors.primary,
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary,
                  },
                }}
              />
            </Grid>
            
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRecruiter}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${
                colors.primary
              } 0%, ${alpha(colors.primary, 0.8)} 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${alpha(
                  colors.primary,
                  0.9
                )} 0%, ${colors.primary} 100%)`,
              },
            }}
          >
            {t('recruiterCard.updateRecruiter')}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  );
}

RecruiterCard.propTypes = {
  recruiterId: PropTypes.string.isRequired,
  imageSrc: PropTypes.string,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  company: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  postal_code: PropTypes.string,
  comp_industry: PropTypes.string,
  comp_description: PropTypes.string,
  onDeleteRecruiter: PropTypes.func.isRequired,
  updateRecruiter: PropTypes.func.isRequired,
  onSelectRecruiter: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(['grid', 'list']),
  isActive: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelection: PropTypes.func,
};

RecruiterCard.defaultProps = {
  imageSrc: '/default-avatar.png',
  company: '',
  address: '',
  city: '',
  country: '',
  postal_code: '',
  comp_industry: '',
  comp_description: '',
  viewMode: 'grid',
  isActive: true,
  isSelectable: false,
  isSelected: false,
  onToggleSelection: null,
};

export default RecruiterCard;